import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { StarIcon } from "@heroicons/react/24/solid";
import { ChangeEvent, useRef } from "react";
import { Loader } from "../index";
import TextInputBase from "../Inputs/TextInputBase";
import { getNestedValue } from "@repo/utilities/object.utility.ts";

type AnchorPosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "bottom start"
  | "bottom end"
  | "top start"
  | "top end";

interface ISearchSelectorProps {
  placeholder?: string;
  label?: string;
  value?: Record<string, any> | null;
  onChange: (value: ISearchSelectorProps["value"]) => void;
  options: Record<any, any>[];
  error?: any;
  disabled?: boolean;
  className?: string;
  accessorKey?: string;
  query: string;
  isRequired?: boolean;
  emptyText?: string;
  validateQuery?: (event: ChangeEvent<HTMLInputElement>) => void;
  setQuery: (value: string) => void;
  isSearching?: boolean;
  secondKey?: string;
  optionsClassName?: string;
  anchorPosition?: AnchorPosition;
}

export default function SearchSelector({
  className,
  label,
  options,
  error,
  value,
  accessorKey = "name",
  secondKey,
  placeholder = "search ...",
  disabled = false,
  setQuery,
  query,
  onChange,
  validateQuery,
  isRequired = true,
  emptyText,
  isSearching = false,
  optionsClassName,
}: ISearchSelectorProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Combobox
      immediate
      value={value}
      onChange={(selectedValue) => {
        onChange(selectedValue);
        setQuery("");
        setTimeout(() => inputRef.current?.blur(), 0);
      }}
      disabled={disabled}
      onClose={() => setQuery("")}
    >
      <div className="relative w-full">
        {label && (
          <label className="text-xs flex items-center gap-1 text-left text-body font-semibold pl-1">
            {label}
            {isRequired && (
              <StarIcon className="size-2 text-error -translate-y-1" />
            )}
          </label>
        )}

        <div className="relative flex items-center">
          <ComboboxInput
            ref={inputRef}
            autoComplete="off"
            value={value ? value[accessorKey] : query}
            as={TextInputBase}
            className={`h-10 w-full text-sm text-body font-semibold px-3 py-1 pr-10 border-2
              ${error ? "border-primary/50" : "border-extraLightGray"}
              placeholder:font-medium placeholder:text-darkGray/80 outline-none rounded-lg
              disabled:bg-extraLightGray/30 disabled:opacity-70 disabled:cursor-not-allowed
              focus:border-primary/50 ${className}`}
            // className={`${className} pr-10  border-none `}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              let value = event.target.value;

              // remove leading spaces
              value = value.replace(/^\s+/, "");

              // replace multiple spaces with single space
              value = value.replace(/\s{2,}/g, " ");
              if (value) onChange(null);
              setQuery(value);
              validateQuery?.(event);
            }}
            placeholder={placeholder}
          />

          <ComboboxButton className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            {!isSearching ? (
              <ChevronUpDownIcon className="h-5 w-5 text-gray" />
            ) : (
              <Loader />
            )}
          </ComboboxButton>
        </div>

        {error?.message && (
          <p className="text-xs text-error font-medium pl-2 mt-0.5">
            {error.message}
          </p>
        )}

        <ComboboxOptions
          as="div"
          transition
          className={`mt-2 w-full absolute shadow-md border bg-white border-extraLightGray
          rounded-md max-h-52 overflow-auto outline-none z-50 ${optionsClassName}`}
        >
          {options?.length > 0 ? (
            options.map((item) => (
              <ComboboxOption
                key={item?.id}
                value={item}
                className="px-4 py-2 text-sm font-semibold transition-colors cursor-pointer select-none flex gap-2
                data-[selected]:text-white data-[selected]:bg-primary
                data-[focus]:text-primary data-[focus]:bg-primary/20"
              >
                {item?.[accessorKey]}

                {secondKey && (
                  <div className="flex gap-2">
                    -
                    <p className="bg-primary/20 text-primary rounded-lg px-2">
                      {getNestedValue(item, secondKey)}
                    </p>
                  </div>
                )}
              </ComboboxOption>
            ))
          ) : (
            <div className="px-4 py-2 text-sm font-semibold text-heading">
              {emptyText || "No Data found!"}
            </div>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
